import { useRef, useState } from 'react';
import { ImageUp, Loader2, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api, ApiError } from '@/lib/api';

interface CompanyLogoFieldProps {
  companyId: string;
  /** Текущая ссылка на логотип; `null` — логотипа нет. */
  logoUrl: string | null;
  /** Новое значение `logo_url` после загрузки/удаления. */
  onChange: (logoUrl: string | null) => void;
  disabled?: boolean;
}

/**
 * Логотип компании: превью, замена, удаление (AK-572).
 *
 * Файл уходит на сервер сразу по выбору, без общей кнопки «Сохранить»: у
 * картинки нет промежуточного состояния, которое имело бы смысл держать в
 * форме, а «выбрал файл, но забыл сохранить» — классический способ потерять
 * работу. Реквизиты рядом сохраняются кнопкой, и это осознанная разница:
 * там правят текст, тут — заменяют целиком.
 *
 * Ошибки сервера показываем дословно: он отвечает по-человечески
 * («картинка слишком маленькая (16×16)»), и переписывать это в
 * «ошибка загрузки» значило бы отнять у человека подсказку.
 */
export function CompanyLogoField({
  companyId,
  logoUrl,
  onChange,
  disabled = false,
}: CompanyLogoFieldProps) {
  const [busy, setBusy] = useState<'upload' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    setError(null);
    setBusy('upload');
    try {
      const form = new FormData();
      form.append('file', file);
      const updated = await api.uploadFile<{ logo_url: string | null }>(
        `/api/v1/companies/${companyId}/logo`,
        form,
      );
      onChange(updated.logo_url ?? null);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || 'Не удалось загрузить логотип'
          : 'Не удалось загрузить логотип',
      );
    } finally {
      setBusy(null);
      // Сбрасываем input — иначе повторный выбор ТОГО ЖЕ файла (после
      // неудачи) не вызовет change и человек решит, что кнопка сломалась.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    setError(null);
    setBusy('delete');
    try {
      const updated = await api.delete<{ logo_url: string | null }>(
        `/api/v1/companies/${companyId}/logo`,
      );
      onChange(updated?.logo_url ?? null);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || 'Не удалось удалить логотип'
          : 'Не удалось удалить логотип',
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="company-logo-input">Логотип компании</Label>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Логотип компании"
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <ImageUp className="h-5 w-5 text-muted-foreground" aria-hidden />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            id="company-logo-input"
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            disabled={disabled || busy !== null}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || busy !== null}
            onClick={() => inputRef.current?.click()}
          >
            {busy === 'upload' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ImageUp className="h-4 w-4" />
            )}
            {logoUrl ? 'Заменить' : 'Загрузить'}
          </Button>

          {logoUrl && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || busy !== null}
              onClick={() => void remove()}
            >
              {busy === 'delete' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Удалить
            </Button>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        PNG, JPEG или WebP до 2 МБ. Показывается в шапке и в печатных формах —
        лучше квадратный или широкий логотип на прозрачном фоне.
      </p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

export default CompanyLogoField;
